import { useEffect, useState } from "react";
import Select from "react-select";
import { useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";
import { redirectToLogin } from "../utils/auth";
import { AsyncPaginate } from "react-select-async-paginate";
import { Loading } from "../Components/Loading/Loading";
import { useUser } from "../context/UserContext";

const tabs = [
  { heading: "Request Details", id: "requestDetails" },
  { heading: "Media Upload", id: "mediaUpload" },
];

// LOA purple theme for the react-select controls (org + collaborators).
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "44px",
    borderRadius: "8px",
    fontSize: "12px",
    borderColor: state.isFocused ? "#3e0084" : "#d1d5db",
    boxShadow: state.isFocused ? "0 0 0 1px #3e0084" : "none",
    "&:hover": { borderColor: "#3e0084" },
  }),
  option: (base, state) => ({
    ...base,
    fontSize: "12px",
    backgroundColor: state.isSelected ? "#3e0084" : state.isFocused ? "#f4f0fa" : "white",
    color: state.isSelected ? "white" : "#141414",
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#f4f0fa",
    borderRadius: "15px",
    border: "1px solid #dcd0f0",
    paddingLeft: "4px",
  }),
  multiValueLabel: (base) => ({ ...base, color: "#3e0084", fontWeight: "bold", fontSize: "12px" }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#3e0084",
    ":hover": { backgroundColor: "#dcd0f0", color: "#3e0084" },
  }),
  placeholder: (base) => ({ ...base, fontSize: "12px" }),
};

// Shared 44px-high control so native inputs/selects line up with react-select.
const controlClass =
  "w-full h-11 border border-gray-300 px-3 rounded-lg text-xs bg-white focus:outline-none focus:border-jiojbmpurple focus:ring-1 focus:ring-jiojbmpurple disabled:bg-gray-50 disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed";

// JBM SLA hours per priority value — drives the auto deadline (date granularity).
const SLA_BY_TAG = {
  jbm: {
    2: { weekday: 2, weekend: 4 },
    1: { weekday: 8, weekend: 8 },
    0: { weekday: 5 * 24, weekend: 7 * 24 },
  },
};

// RCS SLA keyed by severity label: P0 → 4 hours, P1 → 8 hours, P2 → 1 day,
// P3 → 5 days. Hour-level SLAs mean RCS deadlines carry a time (datetime).
const RCS_SLA = {
  P0: { amount: 4, unit: "hours" },
  P1: { amount: 8, unit: "hours" },
  P2: { amount: 1, unit: "days" },
  P3: { amount: 5, unit: "days" },
};

export const Form = () => {
  const [searchParams] = useSearchParams();
  // Pre-select the portal from the dashboard that opened the form (?tag=rcs).
  // Internal users can still switch it. No/unknown param → JBM.
  const initialTag = searchParams.get("tag") === "rcs" ? "rcs" : "jbm";
  const [activeTab, setActiveTab] = useState(0);
  const [tag, setTag] = useState(initialTag);
  const [title, setTitle] = useState("");
  const [description, setDescription] =
    useState(`Please help explain your issue by filling the following fields (whichever are applicable pertaining to the nature of your issue)
Assistant Name(s):
Assistant ID(s):
Request ID(s)/Message ID(s):
Timestamp:
Sample Numbers which faced the issue:
How many numbers faced this issue?:
JSON used (Please attach the file)
Error logs (Please attach the file)
Make and model of the handsets:
Is the issue reproducible?:
Any other details: `);
  const [ticketType, setTicketType] = useState(""); // JBM Type: "0"=Task, "1"=Issue, "other"
  const [requestType, setRequestType] = useState("");
  const [priority, setPriority] = useState("");
  const [issueAt, setIssueAt] = useState("Sandesh");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [requestOptions, setrequestOptions] = useState([]);
  const [priorities, setpriorities] = useState([]);
  const [issueAtOptions, setIssueAtOptions] = useState([]);
  const [ticketCategories, setTicketCategories] = useState([]); // JBM Type: Task/Issue
  const [deadline, setdeadline] = useState("");
  const [status, setstatus] = useState("");
  const [org, setorg] = useState("");
  const [assign, setassign] = useState(
    initialTag === "rcs" ? "Sandesh RCS NNI Support" : "JBM Customer Experience",
  );
  const [submitting, setSubmitting] = useState(false);
  const [formLoading, setFormLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [viewToPartner, setViewToPartner] = useState(false);
  const [collaborators, setCollaborators] = useState([]);

  const authToken = localStorage.getItem("authToken");
  const navigate = useNavigate();

  const { user } = useUser();
  const isAdmin = user.loaded ? !user.isPartner : false;

  useEffect(() => {
    if (!authToken) {
      redirectToLogin();
      return;
    }
    if (!user.loaded) return;

    const BASE = process.env.REACT_APP_BACKEND_URL;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    };

    const get = (url) =>
      fetch(url, { method: "GET", headers }).then((r) => {
        if (r.status === 401) { redirectToLogin(); return null; }
        return r.json();
      });

    const calls = [
      get(`${BASE}/API/V2/config/priorities?tag=${tag}`),
      get(`${BASE}/API/V2/config/requesttypes?tag=${tag}`),
      get(`${BASE}/API/V2/config/issue-at`),
      get(`${BASE}/API/V2/config/ticket-categories`),
      // RCS tickets can be assigned to any user; JBM tickets only to internal staff.
      ...(isAdmin ? [get(`${BASE}/API/V2/users${tag === "rcs" ? "" : "?clientType=internal"}`)] : []),
    ];

    Promise.all(calls)
      .then(([prioritiesData, requestTypesData, issueAtData, ticketCategoriesData, usersData]) => {
        if (!prioritiesData || !requestTypesData) return;
        const sortedPriorities = [...prioritiesData].sort(
          (a, b) => Number(a.value) - Number(b.value)
        );
        setpriorities(sortedPriorities);
        setrequestOptions(requestTypesData);
        setIssueAtOptions(issueAtData || []);
        setTicketCategories(Array.isArray(ticketCategoriesData) ? ticketCategoriesData : []);
        if (isAdmin && usersData) setUsers(usersData);
        setFormLoading(false);
      })
      .catch(console.error);
  }, [user.loaded, tag]);

  useEffect(() => {
    if (!priority) return;

    if (tag === "rcs") {
      // RCS: duration by severity label, kept to the minute (datetime).
      const label = priorities.find((p) => String(p.value) === String(priority))?.key;
      const rule = RCS_SLA[label];
      if (!rule) return;
      setdeadline(moment().add(rule.amount, rule.unit).format("YYYY-MM-DDTHH:mm"));
      return;
    }

    const rule = SLA_BY_TAG[tag]?.[priority];
    if (rule == null) return;
    const currentTime = moment();
    const hoursToAdd = currentTime.isoWeekday() > 5 ? rule.weekend : rule.weekday;
    setdeadline(currentTime.add(hoursToAdd, "hours").format("YYYY-MM-DD"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priority, tag, priorities]);

  const loadOptions = async (searchQuery, loadedOptions, { page }) => {
    if (!isAdmin) return;
    const limit = 50;
    const offset = (page - 1) * limit;

    const res = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/API/V2/getAllOrg?offset=${offset}&limit=${limit}&search=${encodeURIComponent(searchQuery)}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      },
    );
    if (res.status === 401) { redirectToLogin(); return; }
    if (res.status === 500) { alert("Internal Server Error"); redirectToLogin(); return; }

    const data = await res.json();
    const options = data.organizations.map((orgName) => ({ label: orgName, value: orgName }));
    const hasMore = offset + limit < data.totalCount;
    return { options, hasMore, additional: { page: page + 1 } };
  };

  const fileSelectedHandler = (event) => {
    const incoming = Array.from(event.target.files);
    const combined = [...selectedFiles, ...incoming];
    if (combined.length > 10) {
      alert("You can upload a maximum of 10 files.");
    }
    setSelectedFiles(combined.slice(0, 10));
  };

  const removeFileHandler = (index) => {
    setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const save = async () => {
    if (!title || !description || !requestType || !priority) {
      alert("Please fill all required fields.");
      return;
    }
    if (isAdmin && tag === "jbm" && !org) {
      alert("Please fill all required fields.");
      return;
    }
    if (tag === "rcs" && !issueAt) {
      alert("Please fill all required fields.");
      return;
    }

    setSubmitting(true);

    const formData = new FormData();
    formData.append("title", title);
    formData.append("description", description);
    formData.append("requestType", requestType);
    formData.append("priority", priority);
    formData.append("deadline", deadline);
    formData.append("org", tag === "rcs" ? "Rcs NNI" : org.value);
    formData.append("assign", assign);
    formData.append("clientType", tag === "rcs" ? "RCS" : "JBM");
    if (tag === "rcs") formData.append("issueAt", issueAt);
    if (!isAdmin) {
      formData.append("viewToPartner", true);
    } else {
      formData.append("viewToPartner", viewToPartner);
      formData.append("collaborators", collaborators.join(","));
    }

    selectedFiles.forEach((file) => {
      formData.append(`files`, file);
    });

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/API/V2/Tickets`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${authToken}` },
          body: formData,
        },
      );

      const result = await response.json();
      if (response.ok) {
        alert("Form submitted successfully!");
        navigate("/dashboard");
      } else {
        alert(result.error || "Something went wrong.");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("An error occurred while saving the form.");
    } finally {
      setSubmitting(false);
    }
  };

  const userOptions = users.map((u) => ({ value: u.email, label: u.name }));

  // JBM: request types shown depend on the chosen Type. "other" = request types
  // not assigned to any category. RCS: always the full list.
  const filteredRequestOptions =
    tag !== "jbm"
      ? requestOptions
      : ticketType === ""
      ? []
      : ticketType === "other"
      ? requestOptions.filter((o) => !ticketCategories.some((c) => c.key === o.category))
      : requestOptions.filter((o) => o.category === Number(ticketType));

  if (!user.loaded || formLoading) {
    return <Loading />;
  }

  return (
    <div className="flex flex-col p-6 bg-gray-100 min-h-[calc(100vh-72px)]">
      {/* Tab Bar */}
      <div className="flex items-center ml-1 gap-6 mt-4">
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(index)}
            className="text-xs font-bold leading-snug transition-colors relative"
          >
            {tab.heading}
            {activeTab === index && (
              <div className="absolute -bottom-1 left-0 right-0 h-1 rounded-full bg-jiojbmpurple" />
            )}
          </button>
        ))}
        <div className="ml-auto flex items-center">
          <button
            className={`text-xs rounded-3xl px-3 py-1 font-bold tracking-tight leading-tight text-white ${
              submitting
                ? "opacity-50 cursor-not-allowed bg-gray-400"
                : "bg-jiojbmpurple hover:opacity-90"
            }`}
            type="button"
            onClick={submitting ? null : save}
            disabled={submitting}
          >
            {submitting ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {/* Tab: Request Details */}
      {activeTab === 0 && (
        <div className="flex flex-col gap-2 mt-6 ml-1 text-xs">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 grid grid-cols-2 gap-x-4 gap-y-2">
            <div className="flex flex-col gap-1 col-span-2">
              <p>
                Title <span className="text-jiored1">*</span>
              </p>
              <textarea
                className="w-full px-3 py-2.5 border border-gray-300 resize-none rounded-lg text-xs focus:outline-none focus:border-jiojbmpurple focus:ring-1 focus:ring-jiojbmpurple placeholder-gray-500"
                placeholder="Add title here..."
                value={title}
                rows={1}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>
            <div className="flex flex-col gap-1 h-full">
              <p>
                Description <span className="text-jiored1">*</span>
              </p>
              <textarea
                className="w-full flex-1 min-h-40 appearance-none border border-gray-300 px-3 py-2 resize-none rounded-lg text-xs focus:outline-none focus:border-jiojbmpurple focus:ring-1 focus:ring-jiojbmpurple"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              {isAdmin && (
                <div className="flex items-center mt-2">
                  <p className="mr-2">Show Ticket to Partner</p>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={viewToPartner}
                      onChange={() => setViewToPartner(!viewToPartner)}
                    />
                    <div className="w-8 h-4 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:bg-jiojbmpurple transition-colors"></div>
                    <div className="absolute left-1 top-0.5 w-3 h-3 bg-white border border-gray-300 rounded-full transition-transform peer-checked:translate-x-full"></div>
                  </label>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 self-start">
              {isAdmin && (
                <div className="flex flex-col gap-1">
                  <p>
                    Ticket Type <span className="text-jiored1">*</span>
                  </p>
                  <select
                    className={controlClass}
                    value={tag}
                    onChange={(e) => {
                      const nextTag = e.target.value;
                      setTag(nextTag);
                      setTicketType("");
                      setRequestType("");
                      setPriority("");
                      // setIssueAt("");
                      setdeadline("");
                      setassign(
                        nextTag === "rcs"
                          ? "Sandesh RCS NNI Support"
                          : "JBM Customer Experience",
                      );
                    }}
                  >
                    <option value="jbm">JBM</option>
                    <option value="rcs">Sandesh</option>
                  </select>
                </div>
              )}
              {tag === "jbm" && (
                <div className="flex flex-col gap-1">
                  <p>
                    Type <span className="text-jiored1">*</span>
                  </p>
                  <select
                    className={controlClass}
                    value={ticketType}
                    onChange={(e) => {
                      setTicketType(e.target.value);
                      setRequestType("");
                    }}
                  >
                    <option value="">--Select Type--</option>
                    {ticketCategories.map((c) => (
                      <option key={c.key} value={c.key}>
                        {c.value}
                      </option>
                    ))}
                    {/* <option value="other">Other</option> */}
                  </select>
                </div>
              )}
              <div className="flex flex-col gap-1">
                <p>
                  Service Request Type <span className="text-jiored1">*</span>
                </p>
                <select
                  className={controlClass}
                  value={requestType}
                  onChange={(e) => setRequestType(e.target.value)}
                  disabled={tag === "jbm" && !ticketType}
                >
                  <option value="">
                    {tag === "jbm" && !ticketType ? "--Select Type first--" : "--Select Request--"}
                  </option>
                  {filteredRequestOptions
                    .filter((requestOption) => requestOption.isActive !== false)
                    .map((requestOption, index) => (
                    <option key={index} value={requestOption.value}>
                      {requestOption.key}
                    </option>
                  ))}
                </select>
              </div>
              {/* {tag === "rcs" && (
                <div className="flex flex-col gap-1">
                  <p>
                    Issue At <span className="text-jiored1">*</span>
                  </p>
                  <select
                    className={controlClass}
                    value={issueAt}
                    onChange={(e) => setIssueAt(e.target.value)}
                  >
                    <option value="">--Select--</option>
                    {issueAtOptions.map((o, index) => (
                      <option key={index} value={o.value}>
                        {o.key}
                      </option>
                    ))}
                  </select>
                </div>
              )} */}
              <div className="flex flex-col gap-1">
                <p>
                  {tag === "rcs" ? "Severity" : "Priority"}{" "}
                  <span className="text-jiored1">*</span>
                </p>
                <select
                  className={controlClass}
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                >
                  <option value="">--Select Priority--</option>
                  {priorities.map((p, index) => (
                    <option key={index} value={p.value}>
                      {p.key}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <p>
                  Deadline <span className="text-jiored1">*</span>
                </p>
                <input
                  type={tag === "rcs" ? "datetime-local" : "date"}
                  className={controlClass}
                  value={deadline}
                  onChange={(e) => setdeadline(e.target.value)}
                  disabled
                />
              </div>
              <div className="flex flex-col gap-1">
                <p>
                  Status <span className="text-jiored1">*</span>
                </p>
                <select
                  disabled
                  className={controlClass}
                  value={status}
                  onChange={(e) => setstatus(e.target.value)}
                >
                  <option value="0">Draft</option>
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <p>
                  Assign <span className="text-jiored1">*</span>
                </p>
                <select
                  className={controlClass}
                  value={assign}
                  onChange={(e) => setassign(e.target.value)}
                  disabled={!isAdmin}
                >
                  <option>{assign}</option>
                  {users.map((u) => (
                    <option key={u.email} value={u.email}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </div>
              {isAdmin && tag === "jbm" && (
                <div className="flex flex-col gap-1">
                  <p>
                    Organisation <span className="text-jiored1">*</span>
                  </p>
                  <AsyncPaginate
                    value={org}
                    loadOptions={loadOptions}
                    onChange={setorg}
                    additional={{ page: 1 }}
                    placeholder="Select Organisation"
                    isSearchable
                    debounceTimeout={300}
                    className="text-black"
                    styles={selectStyles}
                    loadingMessage={() => "Loading Organisations, please wait..."}
                  />
                </div>
              )}
              {isAdmin && (
                <div className="flex flex-col gap-1 col-span-2">
                  <p>Collaborators</p>
                    <Select
                      isMulti
                      options={userOptions}
                      value={userOptions.filter((option) =>
                        collaborators.includes(option.value),
                      )}
                      onChange={(selected) =>
                        setCollaborators(
                          selected ? selected.map((option) => option.value) : [],
                        )
                      }
                      placeholder="Select collaborators..."
                      className="react-select-container"
                      classNamePrefix="react-select"
                      styles={selectStyles}
                    />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tab: Media Upload */}
      {activeTab === 1 && (
        <div className="flex flex-col gap-2 mt-6 ml-1 text-xs flex-1">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex-1">
            <input
              className="block w-full text-xs text-gray-600 file:mr-3 file:cursor-pointer file:rounded-full file:border-0 file:bg-jiojbmpurple file:px-4 file:py-2 file:font-bold file:text-white hover:file:opacity-90"
              type="file"
              onChange={fileSelectedHandler}
              multiple
              accept=".xls,.xlsx,.pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.json,.csv,.pcap,.pcapng,.mp4,.mov,.avi,.mkv,.webm,.m4v"
            />
            {selectedFiles.length > 0 && (
              <div className="mt-4">
                <p className="font-semibold">Selected Files:</p>
                <ul>
                  {selectedFiles.map((file, index) => (
                    <li
                      key={index}
                      className="flex justify-between items-center border border-gray-300 p-2 px-3 my-1 rounded-lg"
                    >
                      <span>{file.name}</span>
                      <button
                        type="button"
                        className="text-jiored1 hover:text-red-700"
                        onClick={() => removeFileHandler(index)}
                      >
                        ❌
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
